import React, { FC, useEffect, useState } from 'react'
import { RouteComponentProps } from "@reach/router"
import _ from 'lodash'
import Isvg from 'react-inlinesvg'
// Components
import { Toolbar } from "components/subnav"
import ImportIcon from "components/import-export/import-icon"
import ExportIcon from "components/import-export/export-icon"
import Import from "components/import-export/import"
import AreaMeasuresTable from "components/area-measures/area-measures-table"
import RecoveriesAreaMeasuresTable from "components/recoveries/area-measures/area-measures-table"
import { WithTip } from 'components/tooltip/with-tip'
// Data
import api_match from "data/interface/api"
import importService from "data/import"
import areaMeasuresService from "data/area-measures"
import propertyService from 'data/property'
import tenantGroupService from 'data/tenant-group'
import { alphanum } from "components/table/lib/sort"
import { ContextRow } from './area-measures-table'
import toggles from "feature-toggles"
//Styles
import { AreaMeasures_s } from "components/area-measures/area-measures-styles"
import { AddIcon } from "images/icons/tools"

const AreaMeasure: FC<RouteComponentProps> = (props) => {
  const [data, setData] = useState<object[]>([])
  const [initSort, setInitSort] = useState<boolean>(false)
  const [showImport, toggleShowImport] = useState<boolean>(false)
  const [property, setProperty] = useState<object|null>(null);
  const [propertyId, setPropertyId] = useState(propertyService.getStoredPropertyId())
  const [tenantGroups, setTenantGroups] = useState<object[]>([]);
  const tableId = "area-measures-table"
  const [showSelected, setShowSelected] = useState<boolean>(false)
  const [selected, setSelected] = useState<ContextRow[]>([]);

  useEffect(() => {
    importService.stream().subscribe(data => {
      toggleShowImport(data['showImport'])
    })

    toggleShowImport(false);

    const areaMeasuresSubscription = areaMeasuresService.stream().subscribe(data => {
      api_match(
        data,
        init  => setData(init.value),
        ok    => {
          if(!initSort && ok.value.length) { // sort only on first load
            setData([...ok.value].sort((a, b) => alphanum(_.get(a, "code"), _.get(b, "code"))))
            setInitSort(true)
          } else {
            setData(ok.value)
          }
        },
        err   => {
          setData(err.value) // backout any optimistic updates
        },
        fetch => {}
      )
    })

    const propertySubscription = propertyService.stream().subscribe((data) => {
      if (data.value.length > 0){
        const currentProperty = _.find(data.value, {propertyid: data.current});
        setProperty(currentProperty)
      }
      switch (data.state) {
        case 'ok':
          setPropertyId(data.current)
          break
      }
    })

    const tenantGroupSubscription = tenantGroupService.stream().subscribe(data => {
      api_match(
        data,
        init  => setTenantGroups(init.value),
        ok    => {
          if(ok.value.length) { // sort only on first load
            setTenantGroups(ok.value)
          } else {
            setTenantGroups(ok.value)
          }
        },
        err   => {
          setTenantGroups(err.value) // backout any optimistic updates
        },
        fetch => {}
      )
    })

    return function cleanup() {
      areaMeasuresSubscription.unsubscribe()
      propertySubscription.unsubscribe()
      tenantGroupSubscription.unsubscribe()
    }
  }, [initSort])

  useEffect(() => {
    if (_.isNil(propertyId)) return

    areaMeasuresService.fetchAreaMeasures(propertyId)
    propertyService.fetchProperty(propertyId)
    tenantGroupService.fetchTenantGroups(propertyId)

  }, [propertyId])

  const Add = () => {
    return (
      <a onClick={() => areaMeasuresService.create(propertyService.getStoredPropertyId())}>
        <Isvg src={AddIcon} width="20px" />
      </a>
    )
  }

  const [deleteFlag, setDeleteFlag] = useState(0)
  const Delete = () => {
    return (
      <WithTip position="bottom">
        <span>Delete Selected Rows</span>
        <a className={selected.length && !_.some(selected, 'systeminput')  ? '' : 'disabled'}
          onClick={() => {
            if (!selected.length || _.some(selected, 'systeminput')) return
            setDeleteFlag(deleteFlag+1)
        }}>
          <span className="p-menuitem-icon pi pi-fw pi-trash"></span>
        </a>
      </WithTip>
    )
  }

  const [pasteFlag, setPasteFlag] = useState(0)
  const Paste = () => {
    return (
      <WithTip position="bottom">
        <span>Paste to Selected Rows</span>
        <a className={selected.length && !_.some(selected, 'systeminput') ? '' : 'disabled'}
          onClick={() => {
            if (!selected.length || _.some(selected, 'systeminput')) return
            setPasteFlag(pasteFlag+1)
          }
        }>
          <span className="p-menuitem-icon pi pi-fw pi-clone"></span>
        </a>
      </WithTip>
    )
  }

  const ShowSelected = () => (
    <WithTip position="bottom">
      <span>Show {showSelected ? 'All' : 'Selected'} Rows</span>
      <a className={selected.length ? '' : 'disabled'}
        onClick={() => {
          if (!selected.length) return
          setShowSelected(!showSelected)
        }}>
        <span className={showSelected ? 'pi pi-eye-slash' : 'pi pi-eye'} style={{height: '18px', width: '18px'}}></span>
      </a>
    </WithTip>
  )

  useEffect(() => {
    setSelected(_.intersectionBy(data, selected, 'areaid'))
  }, [data])

  useEffect(() => {
    if (showSelected && _.isEmpty(selected)) setShowSelected(false)
  }, [selected])

  const _data = showSelected ? selected : data

  const propertyType = property && property['type'];

  return (
    <AreaMeasures_s>
      <Toolbar>
        <Add />
        {toggles.multiselect() && <>
          <Paste />
          <Delete />
          <ShowSelected />
        </>}
        <ImportIcon />
        <ExportIcon template="area" />
      </Toolbar>
      { showImport && <Import /> }
      { propertyType !== 'Commercial' && data && property && !showImport && <AreaMeasuresTable tableId={tableId} data={_data} selectedState={[selected, setSelected]} flags={{deleteFlag, pasteFlag}}/> }
      { propertyType === 'Commercial' && data && property && !showImport && tenantGroups && <RecoveriesAreaMeasuresTable tableId={tableId} data={_data} property={property} tenantGroups={tenantGroups} selectedState={[selected, setSelected]} flags={{deleteFlag, pasteFlag}}/> }
    </AreaMeasures_s>
  )
}

export default AreaMeasure
